import { Product } from 'utils/helpers/product';

export function getTagNamesFromProducts(products: Product[]): string[] {
  const seen = new Map<string, string>();

  products.forEach((product) => {
    product.POSMetaData?.canonicalProductTags?.forEach((tag) => {
      if (!tag?.tagId || !tag?.tagName) {
        return;
      }
      const key = tag.tagName.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, tag.tagName);
      }
    });
  });

  return Array.from(seen.values());
}

export function compareTagNamesNaturally(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

export function tagNameMatchesAny(tagName: string, matchStrings: string[]): boolean {
  const lowerTagName = tagName.toLowerCase();
  return matchStrings.some((match) => lowerTagName.includes(match.toLowerCase()));
}

export function filterTagNamesByMatch(tagNames: string[], matchStrings: string[]): string[] {
  return tagNames.filter((tagName) => tagNameMatchesAny(tagName, matchStrings));
}

/**
 * Does this product carry any of the exact, currently-selected tag names? Single source of
 * truth for "does the active tags filter apply to this product" — used both for the organic
 * product list (`use-filter-products-query-by-tags`) and for sponsored/ad candidates
 * (`useSponsoredProductsFilter`), so a sponsored product can never bypass the tags filter the
 * way it bypassed it before this predicate existed. An empty `selectedTags` means the filter
 * isn't active, so every product passes.
 */
export function productMatchesSelectedTags(product: Product, selectedTags: string[]): boolean {
  if (!selectedTags.length) {
    return true;
  }

  const normalizedSelected = new Set(selectedTags.map((tag) => tag.toLowerCase()));
  const productTagNames = product.POSMetaData?.canonicalProductTags
    ?.map((tag) => tag?.tagName?.toLowerCase())
    .filter((tagName): tagName is string => Boolean(tagName));

  return Boolean(productTagNames?.some((tagName) => normalizedSelected.has(tagName)));
}
